/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import i18n from '../i18n'

const LanguageContext = createContext(null)

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('language') || 'en'
  })
  const [isRTL, setIsRTL] = useState(() => {
    const lang = localStorage.getItem('language') || 'en'
    return ['ur', 'sd'].includes(lang)
  })

  const setLanguage = useCallback((lang) => {
    if (['en', 'ur', 'sd'].includes(lang)) {
      setLanguageState(lang)
      setIsRTL(['ur', 'sd'].includes(lang))
      localStorage.setItem('language', lang)
      i18n.changeLanguage(lang)
      document.documentElement.lang = lang
      document.documentElement.dir = ['ur', 'sd'].includes(lang) ? 'rtl' : 'ltr'
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
  }, [language, isRTL])

  const translate = useCallback((key, options) => {
    const translateKey = typeof key === 'string' ? key : String(key)
    const fixedT = i18n.getFixedT(language)
    const result = fixedT(translateKey, options)

    if (result && result !== translateKey) {
      return result
    }

    if (options?.defaultValue) {
      return options.defaultValue
    }

    const lastSegment = translateKey.split('.').pop() || translateKey
    return lastSegment.replace(/[_-]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }, [language])

  const value = useMemo(() => ({
    language,
    isRTL,
    setLanguage,
    t: translate,
    languages: [
      { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
      { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰' },
      { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', flag: '🇸🇩' },
    ],
  }), [language, isRTL, setLanguage, translate])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}